
import React, {useState, useEffect} from 'react';

import {
  createInstance,
  OptimizelyProvider,
  enums,
} from '@optimizely/react-sdk';
import {get, omitBy} from 'lodash-es';
import PropTypes from 'prop-types';

import subjects from 'constants/subjects';
import {isNonProdEnvironment} from 'utils/environment';

function formatTeacherSubjects(teacher) {
  // Convert array of all subjects into Optimizely attributes object.
  return subjects.reduce(
    (attributesMap, subject) => {
      const userTeachersSubject = teacher?.subjects?.includes(subject);
      const kebabCaseSubject = subject.replace(/\W/g, '-');
      return {
        ...attributesMap,
        [`${kebabCaseSubject}-Instructor`]: userTeachersSubject
      };
    },
    {}
  );
}

function getOptimizelyAttributes({userProperties, user}) {
  if (!user || !userProperties) return {};

  const {
    isStudent, isTeacher, isFreeUser, isSubscription, isTrialUser, activeELALicense,
    activeEssentialsLicense, activeSSLicense, activeScienceLicense
  } = userProperties;
  const {is_staff, grade_level} = user;

  const userId = user.id && user.id.toString();
  const schoolId = (user.schools && user.schools.length) ? user.schools[0].id.toString() : '';
  const state = get(user, 'teacher.school.address.state', get(user, 'address.state'));

  const subjects = formatTeacherSubjects(user.teacher);

  return omitBy({/** Note:
    * $opt_bucketing_id appears in the DECISION_SERVICE logs as the
    * bucketingId but the BUCKETER will always logs the userId as
    * bucketingId
    */
    ...subjects,
    $opt_bucketing_id: schoolId,
    isFreeUser: isFreeUser,
    isSubscription: isSubscription,
    isStaff: is_staff,
    isTrialUser: isTrialUser,
    schoolId: schoolId,
    state: state,
    userId: userId,
    gradeLevel: grade_level,
    isStudent: isStudent,
    isTeacher: isTeacher,
    activeELALicense: activeELALicense,
    activeEssentialsLicense: activeEssentialsLicense,
    activeSSLicense: activeSSLicense,
    activeScienceLicense: activeScienceLicense
  }, (value) => value === '' || typeof value === 'undefined');
}

const hopscotchExperimentEventConverter = (eventStreamEvent) => {
  const mappedOptimizelyEvents = [];
  const optimizelyMap = {
    'Instructional note click': [
      'Content Initiate Read click',
      // TODO: Check on 'resource panel click event names.
    ],
    'Content impression': [
      'Content Navigate impression',
    ],
    'Content click': [
      'Content Navigate click',
    ],
    'Assignment created': [
      'Assignment Initiate Create click',
      'Assignment Initiate Update click',
      'Assignment Create click',
    ],
  };

  for (const optimizelyEventKey of Object.keys(optimizelyMap)) {
    // Allow for greater specificity in event mapper by flattening to include event type.
    const flattenedEventStreamName = `${eventStreamEvent.event_name} ${eventStreamEvent.dimension_properties.event_type}`;

    if (optimizelyMap[optimizelyEventKey].includes(flattenedEventStreamName)) {
      mappedOptimizelyEvents.push(optimizelyEventKey);
    }
  }

  // Array of objects with optimizely event name and tags
  return mappedOptimizelyEvents;
};

const eventConverters = [
  hopscotchExperimentEventConverter
];

const convertToOptimizelyEventAndSend = (eventStreamEvent, config) => {
  const optimizelyEventKeysToEmit = [];

  for (const converter of eventConverters) {
    try {
      const optimizelyEventKeysForThisConverter = converter(eventStreamEvent);
      optimizelyEventKeysToEmit.push(...optimizelyEventKeysForThisConverter);
    } catch (error) {
      // TODO: log warning to sentry
      // eslint-disable-next-line
      console.error(error);
    }
  }

  try {
    for (const optimizelyEventKey of optimizelyEventKeysToEmit) {
      const optimizelyEventTags = {eventStreamId: eventStreamEvent.id};
      config.optimizely.track(optimizelyEventKey, undefined, undefined, optimizelyEventTags);
    }
  } catch (error) {
    // TODO: log warning to sentry
    // eslint-disable-next-line
    console.error(error);
  }
};

const createOptimizelyInstance = (options) => {
  return createInstance({
    sdkKey: window.OPTIMIZELY_SDK_KEY,
    // Can set log level to LOG_LEVEL.DEBUG for addt'l logs from Optimizely
    logLevel: isNonProdEnvironment() ? enums.LOG_LEVEL.INFO : enums.LOG_LEVEL.ERROR,
    ...options,
  });
};

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number
  }),
  userProperties: PropTypes.object,
  children: PropTypes.node,
};

const NewselaOptimizelyProvider = ({user, userProperties, children}) => {
  const userId = user && user.id && user.id.toString();
  const optimizelyAttributes = getOptimizelyAttributes({userProperties, user});

  const [optimizely, setOptimizely] = useState(createOptimizelyInstance);
  useEffect(() => {
    optimizely.onReady().then((result) => {
      const {success} = result;
      const optConfig = optimizely.getOptimizelyConfig();
      if (success && optConfig) return;
      // If the regular Optimizely CDN request fails, use a custom made
      // endpoint on our backend that proxies to Optimizely. This helps enable
      // Optimizely variation logic in the face of ad / content blockers. It
      // does NOT prevent such blockers from blocking decision or conversion
      // events we attempt to send to Optimizely.
      setOptimizely(createOptimizelyInstance({
        datafileOptions: {
          urlTemplate: '/api/v3/optimizely/datafiles/%s'
        },
      }));
    });
  }, []);

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      user={{
        id: userId,
        attributes: optimizelyAttributes
      }}
    >
      {children}
    </OptimizelyProvider>
  );
};

NewselaOptimizelyProvider.propTypes = propTypes;

export {getOptimizelyAttributes, convertToOptimizelyEventAndSend, NewselaOptimizelyProvider};
